import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UsersService } from 'core/services/users.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { JourneyTabComponent } from './journey-tab/journey-tab.component';
import { PersonalInformationTabComponent } from './personal-information-tab/personal-information-tab.component';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { TabsV2Component } from 'shared/components/tabs-v2/tabs-v2.component';
import { User } from 'shared/types/user.model';
import { ReferralClipboardService } from 'core/services/referral-clipboard.service';
import { UserAvatarV2Component } from 'shared';
import { getUserAvatarUrl } from 'shared/utils/user-avatar.utils';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { SortOrders } from 'shared/types/sort-order.enum';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';

enum ETab {
  JOURNEY,
  PERSONAL_INFORMATION
}

@Component({
  selector: 'app-user-info',
  imports: [
    KorviuLoaderComponent,
    DatePipe,
    TranslatePipe,
    TabsV2Component,
    JourneyTabComponent,
    PersonalInformationTabComponent,
    UserAvatarV2Component,
    ResourceLoadErrorComponent
  ],
  templateUrl: './user-info.component.html',
  styleUrl: './user-info.component.scss'
})
export class UserInfoComponent {
  #usersService = inject(UsersService);
  #paymentAccountsService = inject(PaymentAccountsService);
  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);
  readonly #referralClipboard = inject(ReferralClipboardService);
  #translateService = inject(TranslateService);

  ETab = ETab;

  userResource = this.#usersService.getMe();
  userPaymentAccountsResource = this.#paymentAccountsService.userPaymentAccountsResource();

  user = computed<User | null>(() => (this.userResource.hasValue() ? this.userResource.value() : null));
  readonly profileAvatarUrl = computed(() => getUserAvatarUrl(this.user()?.avatar?.image, 'profile'));
  paymentAccounts = computed(() =>
    this.userPaymentAccountsResource.hasValue() ? this.userPaymentAccountsResource.value() || [] : []
  );
  readonly recentActivities = toSignal(
    this.#translateService.onLangChange.pipe(
      startWith(null),
      switchMap(() =>
        this.#usersService
          .getRecentActivity({ page: 1, pageSize: 3 }, [], { sortBy: 'date', sortOrder: SortOrders.DESCEND })
          .pipe(
            map(response => response.results),
            catchError(() => of([]))
          )
      )
    ),
    { initialValue: [] }
  );
  isLoading = computed(() => this.userResource.isLoading());

  tabs = signal([
    { id: ETab.JOURNEY, label: 'profile.journey' },
    { id: ETab.PERSONAL_INFORMATION, label: 'profile.personalInformation' }
  ]);
  selectedTab = signal<ETab>(ETab.JOURNEY);

  copyReferralCode(): void {
    void this.#referralClipboard.copy(this.user()?.referralCode);
  }

  goToRecentActivity(): void {
    this.#router.navigate(['recent-activity'], { relativeTo: this.#activatedRoute });
  }
}
